import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { PersonAvatar } from '../components/PersonAvatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Search, Edit, Trash2, Eye, MoreVertical, UserCog, Loader2, Mail, Phone, Briefcase, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { exportToExcel } from '../lib/exportUtils';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const EmployeesPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('employees.create');
  const canEdit = hasPermission('employees.edit');
  const canDelete = hasPermission('employees.delete');
  const [employees, setEmployees] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', designation: '',
    department: '', joining_date: '', salary: '', joining_ctc: '', status: 'active',
    work_days: [0, 1, 2, 3, 4], overtime_rate: '0', standard_hours: '8',
    basic_percent: '50', hra_percent: '25',
    work_start_time: '09:00', work_end_time: '18:00',
  });

  useEffect(() => { fetchEmployees(); }, [deptFilter, statusFilter, searchQuery]);

  useEffect(() => {
    // Departments now come from /api/departments (configurable in Settings).
    api.get('/departments').then(r => {
      if (r.data?.success) {
        setDepartmentOptions(
          (r.data.data || []).map(d => ({ value: d.name, label: d.name }))
        );
      }
    }).catch(() => { /* dropdown silently empty if fetch fails */ });
    // Designations: STRICT picker. Each option carries its department so
    // selecting a designation auto-fills the (read-only) department field.
    api.get('/designations').then(r => {
      if (r.data?.success) {
        setDesignationOptions(
          (r.data.data || []).map(d => ({
            value: d.name, label: d.name, department: d.department || '',
          }))
        );
      }
    }).catch(() => {});
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      let url = '/employees?limit=200';
      if (searchQuery) url += `&search=${searchQuery}`;
      if (deptFilter !== 'all') url += `&department=${deptFilter}`;
      // statusFilter values: 'active' (default) | 'inactive' | 'all'.
      // 'inactive' covers everything except active (on_leave, terminated, …)
      if (statusFilter === 'active') url += `&status=active`;
      const response = await api.get(url);
      if (response.data.success) {
        let rows = response.data.data;
        if (statusFilter === 'inactive') rows = rows.filter(e => e.status !== 'active');
        setEmployees(rows);
      }
    } catch {
      toast.error('Failed to load employees');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const salaryNum = parseFloat(formData.salary) || 0;
    const joiningNum = parseFloat(formData.joining_ctc) || 0;
    const payload = {
      ...formData,
      salary: salaryNum,
      // Auto-copy: if user left joining_ctc blank, default to current CTC
      // (day-1 case — they're the same until appraisals diverge them).
      joining_ctc: joiningNum > 0 ? joiningNum : salaryNum,
      overtime_rate: parseFloat(formData.overtime_rate) || 0,
      standard_hours: parseFloat(formData.standard_hours) || 8,
    };
    try {
      if (isEditing && selectedEmployee) {
        await api.put(`/employees/${selectedEmployee.id}`, payload);
        // Also update work settings
        await api.put(`/attendance/employee-settings/${selectedEmployee.id}`, {
          work_days: payload.work_days,
          overtime_rate: payload.overtime_rate,
          standard_hours: payload.standard_hours,
        });
        toast.success('Employee updated');
      } else {
        // New employees default to inactive account_status
        payload.account_status = 'inactive';
        await api.post('/employees', payload);
        toast.success('Employee added');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleDelete = async (emp) => {
    const confirmed = await confirm({ title: 'Remove Employee', description: `Are you sure you want to remove ${emp.first_name} ${emp.last_name}? This action cannot be undone.` });
    if (!confirmed) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success('Employee removed');
      fetchEmployees();
    } catch {
      toast.error('Failed to delete employee');
    }
  };

  const handleEdit = (emp) => {
    setSelectedEmployee(emp);
    setFormData({
      first_name: emp.first_name, last_name: emp.last_name, email: emp.email,
      phone: emp.phone || '', designation: emp.designation || '', department: emp.department || '',
      joining_date: emp.joining_date ? emp.joining_date.split('T')[0] : '',
      salary: String(emp.salary || ''), joining_ctc: String(emp.joining_ctc || ''), status: emp.status || 'active',
      work_days: emp.work_days || [0, 1, 2, 3, 4],
      overtime_rate: String(emp.overtime_rate || '0'),
      standard_hours: String(emp.standard_hours || '8'),
      basic_percent: String(emp.basic_percent || '50'),
      hra_percent: String(emp.hra_percent || '25'),
      work_start_time: emp.work_start_time || '09:00',
      work_end_time: emp.work_end_time || '18:00',
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', email: '', phone: '', designation: '',
      department: '', joining_date: '', salary: '', joining_ctc: '', status: 'active',
      work_days: [0, 1, 2, 3, 4], overtime_rate: '0', standard_hours: '8',
      work_start_time: '09:00', work_end_time: '18:00',
    });
    setSelectedEmployee(null);
    setIsEditing(false);
  };

  const toggleWorkDay = (day) => {
    setFormData(prev => ({
      ...prev,
      work_days: prev.work_days.includes(day)
        ? prev.work_days.filter(d => d !== day)
        : [...prev.work_days, day].sort(),
    }));
  };

  const getStatusBadge = (status) => {
    const cfg = { active: 'bg-emerald-500/10 text-emerald-500', on_leave: 'bg-amber-500/10 text-amber-500', terminated: 'bg-red-500/10 text-red-500' };
    return <Badge className={cfg[status] || cfg.active}>{(status || '').replace('_', ' ')}</Badge>;
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Employees</h1><p className="text-muted-foreground">Manage your team members</p></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportToExcel(employees, [
            { key: 'employee_id', header: 'ID' }, { key: 'first_name', header: 'First Name' },
            { key: 'last_name', header: 'Last Name' }, { key: 'email', header: 'Email' },
            { key: 'designation', header: 'Designation' }, { key: 'department', header: 'Department' },
            { key: 'status', header: 'Status' },
          ], 'Employees')} data-testid="export-employees-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
            {canCreate && (<DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-employee-btn"><Plus className="h-4 w-4 mr-2" />Add Employee</Button></DialogTrigger>)}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
              <DialogHeader><DialogTitle>{isEditing ? 'Edit Employee' : 'Add Employee'}</DialogTitle><DialogDescription>Enter employee details</DialogDescription></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>First Name *</Label><Input value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} required data-testid="employee-first-name" /></div>
                  <div className="space-y-2"><Label>Last Name *</Label><Input value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} required data-testid="employee-last-name" /></div>
                  <div className="space-y-2"><Label>Email *</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required data-testid="employee-email" /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} /></div>
                  <div className="space-y-2">
                    <Label>Designation *</Label>
                    <Select
                      value={formData.designation || ''}
                      onValueChange={v => {
                        const match = designationOptions.find(o => o.value === v);
                        setFormData({
                          ...formData,
                          designation: v,
                          department: match?.department || '',
                        });
                      }}
                    >
                      <SelectTrigger data-testid="employee-designation"><SelectValue placeholder="Select designation" /></SelectTrigger>
                      <SelectContent>
                        {designationOptions.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}{o.department ? <span className="text-xs text-muted-foreground ml-2">· {o.department}</span> : null}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!designationOptions.some(o => o.value === formData.designation) && formData.designation ? (
                      <p className="text-xs text-amber-500" data-testid="employee-designation-legacy">
                        Legacy title "{formData.designation}" is not in the master list — please re-pick.
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Input
                      value={formData.department || ''}
                      readOnly
                      tabIndex={-1}
                      data-testid="employee-department"
                      placeholder="Auto-filled from designation"
                      className="bg-muted/40 cursor-not-allowed"
                    />
                    <p className="text-xs text-muted-foreground">Derived from the selected designation.</p>
                  </div>
                  <div className="space-y-2"><Label>Joining Date</Label><Input type="date" value={formData.joining_date} onChange={e => setFormData({ ...formData, joining_date: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Annual CTC (INR)</Label><Input type="number" value={formData.salary} onChange={e => setFormData({ ...formData, salary: e.target.value })} data-testid="employee-salary" /></div>
                  <div className="space-y-2">
                    <Label>Joining CTC (INR)</Label>
                    <Input
                      type="number"
                      value={formData.joining_ctc}
                      onChange={e => setFormData({ ...formData, joining_ctc: e.target.value })}
                      placeholder={formData.salary ? `Auto-fills to ${formData.salary} if blank` : 'Optional'}
                      data-testid="employee-joining-ctc"
                    />
                    <p className="text-xs text-muted-foreground">CTC at the time of joining. Stays anchored as appraisals raise the current CTC.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Basic %</Label>
                    <Select value={formData.basic_percent} onValueChange={v => setFormData({ ...formData, basic_percent: v })}>
                      <SelectTrigger data-testid="employee-basic-pct"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="40">40%</SelectItem><SelectItem value="45">45%</SelectItem><SelectItem value="50">50%</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>HRA %</Label>
                    <Select value={formData.hra_percent} onValueChange={v => setFormData({ ...formData, hra_percent: v })}>
                      <SelectTrigger data-testid="employee-hra-pct"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="20">20%</SelectItem><SelectItem value="25">25%</SelectItem><SelectItem value="30">30%</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {(() => {
                    const ctc = parseFloat(formData.salary) || 0;
                    if (ctc <= 0) return null;
                    const basicPct = parseFloat(formData.basic_percent) || 50;
                    const hraPct = parseFloat(formData.hra_percent) || 25;
                    const basicM = Math.round((ctc * basicPct / 100) / 12);
                    const hraM = Math.round((ctc * hraPct / 100) / 12);
                    const convM = 1600;
                    const medM = 1250;
                    const ptM = 200;
                    const specialM = Math.round(ctc / 12) - basicM - hraM - convM - medM;
                    const effectiveM = Math.round(ctc / 12) - ptM;
                    return (
                      <div className="col-span-2 p-3 rounded-lg bg-muted/30 space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Basic</span><span>{formatCurrency(basicM)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">HRA</span><span>{formatCurrency(hraM)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Conveyance</span><span>{formatCurrency(convM)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Medical</span><span>{formatCurrency(medM)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Special Allowance</span><span>{formatCurrency(Math.max(specialM, 0))}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Prof. Tax</span><span className="text-red-400">-{formatCurrency(ptM)}</span></div>
                        <div className="flex justify-between border-t pt-1 mt-1"><span className="font-semibold text-primary">Effective Monthly</span><span className="font-semibold text-primary">{formatCurrency(effectiveM)}</span></div>
                      </div>
                    );
                  })()}
                </div>

                {/* Work Schedule Section */}
                <div className="border-t border-border pt-4 space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Work Schedule & Overtime</h3>
                  <div className="space-y-2">
                    <Label>Working Days</Label>
                    <div className="flex gap-2" data-testid="work-days-selector">
                      {DAY_LABELS.map((label, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => toggleWorkDay(idx)}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            formData.work_days.includes(idx)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                          data-testid={`work-day-${idx}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">{formData.work_days.length} days/week selected</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Standard Hours/Day</Label>
                      <Input type="number" step="0.5" value={formData.standard_hours} onChange={e => setFormData({ ...formData, standard_hours: e.target.value })} data-testid="employee-standard-hours" />
                    </div>
                    <div className="space-y-2">
                      <Label>Overtime Rate (per day multiplier)</Label>
                      <Input type="number" step="0.5" min="0" value={formData.overtime_rate} onChange={e => setFormData({ ...formData, overtime_rate: e.target.value })} data-testid="employee-overtime-rate" />
                      {(() => {
                        const sal = (parseFloat(formData.salary) || 0) / 12;
                        const rate = parseFloat(formData.overtime_rate) || 0;
                        const wdPerWeek = formData.work_days.length || 5;
                        if (sal > 0 && rate > 0) {
                          const weekendDays = Math.floor((30 / 7) * (7 - wdPerWeek));
                          const workingDays = 30 - weekendDays;
                          const perDay = sal / workingDays;
                          const otAmount = Math.ceil((perDay * rate) / 50) * 50;
                          return <p className="text-xs text-muted-foreground mt-1">Approx. per day: <span className="font-semibold text-emerald-500">&#8377;{otAmount.toLocaleString('en-IN')}</span> ({workingDays} working days/month)</p>;
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Office Start Time</Label>
                      <Input type="time" value={formData.work_start_time} onChange={e => setFormData({ ...formData, work_start_time: e.target.value })} data-testid="employee-work-start-time" />
                    </div>
                    <div className="space-y-2">
                      <Label>Office End Time</Label>
                      <Input type="time" value={formData.work_end_time} onChange={e => setFormData({ ...formData, work_end_time: e.target.value })} data-testid="employee-work-end-time" />
                    </div>
                  </div>
                </div>

                <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="employee-submit-btn">{isEditing ? 'Update' : 'Add'}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card><CardContent className="py-4"><div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search employees..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="employee-search" /></div>
        <Select value={deptFilter} onValueChange={setDeptFilter}><SelectTrigger className="w-full sm:w-[180px]" data-testid="employee-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger><SelectContent><SelectItem value="all">All Departments</SelectItem>{departmentOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="employee-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All Statuses</SelectItem>
          </SelectContent>
        </Select>
      </div></CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><UserCog className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold" data-testid="emp-total">{employees.length}</p><p className="text-xs text-muted-foreground">Total</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><UserCog className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold" data-testid="emp-active">{employees.filter(e => e.status === 'active').length}</p><p className="text-xs text-muted-foreground">Active</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><UserCog className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold" data-testid="emp-leave">{employees.filter(e => e.status === 'on_leave').length}</p><p className="text-xs text-muted-foreground">On Leave</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><Briefcase className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold" data-testid="emp-depts">{new Set(employees.map(e => e.department)).size}</p><p className="text-xs text-muted-foreground">Departments</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        employees.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><UserCog className="h-12 w-12 mb-4 opacity-50" /><p>No employees found</p></div> :
        <Table data-testid="employees-table"><TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Designation</TableHead><TableHead>Department</TableHead><TableHead>Salary</TableHead><TableHead>Work Days</TableHead><TableHead>Last Check-in</TableHead><TableHead>Status</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{employees.map(emp => {
          const isInactive = emp.status !== 'active';
          return (
          <TableRow
            key={emp.id}
            data-testid={`employee-row-${emp.id}`}
            className={isInactive ? 'opacity-60' : ''}
          >
            <TableCell><div className="flex items-center gap-3"><PersonAvatar firstName={emp.first_name} lastName={emp.last_name} picture={emp.profile_picture} seed={emp.id} size="md" /><div><Link to={`/employees/${emp.id}`} className="font-medium text-foreground hover:text-primary hover:underline transition-colors" data-testid={`employee-name-link-${emp.id}`}>{emp.first_name} {emp.last_name}</Link><p className="text-xs text-muted-foreground">{emp.employee_id}</p></div></div></TableCell>
            <TableCell>{emp.designation}</TableCell>
            <TableCell><Badge variant="outline">{emp.department}</Badge></TableCell>
            <TableCell className="font-mono text-sm">{formatCurrency(emp.salary)}</TableCell>
            <TableCell>
              <div className="flex gap-0.5">
                {DAY_LABELS.map((d, i) => (
                  <span key={i} className={`w-5 h-5 rounded text-[9px] font-medium flex items-center justify-center ${
                    (emp.work_days || [0,1,2,3,4]).includes(i) ? 'bg-primary/20 text-primary' : 'bg-muted/30 text-muted-foreground/40'
                  }`}>{d[0]}</span>
                ))}
              </div>
            </TableCell>
            <TableCell data-testid={`employee-last-checkin-${emp.id}`}>
              {emp.last_check_in_at ? (
                <div className="text-xs">
                  <div className="font-medium">{emp.last_check_in_time || '—'}</div>
                  <div className="text-muted-foreground">{new Date(emp.last_check_in_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                </div>
              ) : <span className="text-xs text-muted-foreground">Never</span>}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1.5 flex-wrap">
                {getStatusBadge(emp.status)}
                {emp.attendance_exempt && (
                  <Badge className="bg-violet-500/10 text-violet-400 border border-violet-500/30 text-[10px] py-0 px-1.5" title="Attendance-exempt — payroll assumes full month of attendance" data-testid={`employee-exempt-badge-${emp.id}`}>Exempt</Badge>
                )}
              </div>
            </TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`employee-actions-${emp.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/employees/${emp.id}`)}><Eye className="h-4 w-4 mr-2" />View Profile</DropdownMenuItem>
                {canEdit && (<DropdownMenuItem onClick={() => handleEdit(emp)} data-testid={`employee-edit-${emp.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                {canDelete && (<DropdownMenuItem onClick={() => handleDelete(emp)} className="text-destructive focus:text-destructive" data-testid={`employee-delete-${emp.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>);
        })}</TableBody></Table>}
      </CardContent></Card>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Employee Details</DialogTitle></DialogHeader>
          {selectedEmployee && <div className="space-y-4">
            <div className="flex items-center gap-4"><PersonAvatar firstName={selectedEmployee.first_name} lastName={selectedEmployee.last_name} picture={selectedEmployee.profile_picture} size="xl" /><div><h3 className="text-xl font-bold">{selectedEmployee.first_name} {selectedEmployee.last_name}</h3><p className="text-sm text-muted-foreground">{selectedEmployee.designation}</p>{getStatusBadge(selectedEmployee.status)}</div></div>
            <div className="grid grid-cols-2 gap-4 text-sm pt-2 border-t border-border">
              <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /><span>{selectedEmployee.email}</span></div>
              <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-4 w-4" /><span>{selectedEmployee.phone}</span></div>
              <div><p className="text-muted-foreground">Department</p><p className="font-semibold">{selectedEmployee.department}</p></div>
              <div><p className="text-muted-foreground">Annual CTC</p><p className="font-semibold">{formatCurrency(selectedEmployee.salary)}</p></div>
              <div><p className="text-muted-foreground">Overtime Rate</p><p className="font-semibold">{selectedEmployee.overtime_rate || 0}x per day</p></div>
              <div><p className="text-muted-foreground">Standard Hours</p><p className="font-semibold">{selectedEmployee.standard_hours || 8}h/day</p></div>
              <div className="col-span-2"><p className="text-muted-foreground mb-1">Work Days</p>
                <div className="flex gap-1">
                  {DAY_LABELS.map((d, i) => (
                    <span key={i} className={`px-2 py-1 rounded text-xs font-medium ${
                      (selectedEmployee.work_days || [0,1,2,3,4]).includes(i) ? 'bg-primary/20 text-primary' : 'bg-muted/30 text-muted-foreground/40'
                    }`}>{d}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
